import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Collapse,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TimerIcon from "@mui/icons-material/Timer";
import RouteIcon from "@mui/icons-material/Route";
import SpeedIcon from "@mui/icons-material/Speed";
import {
  GoogleMap,
  Polyline,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { format } from "date-fns";
import { mapStyles, mapOptions } from "./vehicleTrack/styles";
import {
  formatTime,
  getUserTimeFormat,
  getUserDateFormat,
} from "../../utils/formatDate.js";

const miniMapContainerStyle = {
  width: "100%",
  height: "100%",
  minHeight: 400,
};

// ─── Shared date/time helpers ─────────────────────────────────────────────────

/**
 * Format a "YYYY/MM/DD" trip date string using the user's date format preference.
 * Mirrors the formatDateOnly pattern used in AlertItem.
 */
const formatTripDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    // API returns DD/MM/YYYY — parse parts explicitly to avoid browser mis-interpretation
    const parts = dateStr.split("/");
    let dateObj;
    if (parts.length === 3) {
      const isYearFirst = parts[0].length === 4; // YYYY/MM/DD
      const [year, month, day] = isYearFirst
        ? [parseInt(parts[0], 10), parseInt(parts[1], 10), parseInt(parts[2], 10)]
        : [parseInt(parts[2], 10), parseInt(parts[1], 10), parseInt(parts[0], 10)]; // DD/MM/YYYY
      dateObj = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      dateObj = new Date(dateStr);
    }
    if (isNaN(dateObj.getTime())) return dateStr;

    const userDateFormat = getUserDateFormat();
    let compactFormat;
    switch (userDateFormat) {
      case "MM/DD/YYYY":
        compactFormat = "MMM dd, yyyy";
        break;
      case "YYYY/MM/DD":
        compactFormat = "yyyy MMM dd";
        break;
      case "DD/MM/YYYY":
      default:
        compactFormat = "dd MMM yyyy";
        break;
    }
    return format(dateObj, compactFormat);
  } catch (error) {
    console.error("Error formatting trip date:", error);
    return dateStr;
  }
};

/**
 * Format a "HH:MM:SS" time string using the user's time format preference.
 * Needed because trip times arrive as plain strings, not Date objects.
 */
const formatTripTime = (timeStr) => {
  if (!timeStr) return "";
  const timeFormat = getUserTimeFormat();
  if (timeFormat !== "12hr") return timeStr; // return as-is for 24hr
  const [hours, minutes, seconds] = timeStr.split(":");
  const h = parseInt(hours, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = ((h + 11) % 12) + 1;
  return seconds
    ? `${hour12}:${minutes}:${seconds} ${suffix}`
    : `${hour12}:${minutes} ${suffix}`;
};

/**
 * Format a timestamp (ms) into date string using user's date format preference.
 * Used for waypoint dots on the mini map.
 */
const formatWaypointDate = (timestamp) => {
  try {
    const dateObj = new Date(timestamp);
    const userDateFormat = getUserDateFormat();
    let compactFormat;
    switch (userDateFormat) {
      case "MM/DD/YYYY": compactFormat = "MMM dd, yyyy"; break;
      case "YYYY/MM/DD": compactFormat = "yyyy MMM dd";  break;
      default:           compactFormat = "dd MMM yyyy";  break;
    }
    return format(dateObj, compactFormat);
  } catch {
    return new Date(timestamp).toDateString();
  }
};

// ─── Haversine helper ─────────────────────────────────────────────────────────
const haversineMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** Sample historicalPath at ~intervalMs gaps using timestamps */
const sampleWaypoints = (path, intervalMs = 30000) => {
  if (!path || path.length === 0) return [];

  const result = [];
  let lastTs = path[0].timestamp - intervalMs;

  path.forEach((pt, idx) => {
    if (pt.timestamp - lastTs >= intervalMs) {
      let speedKmh = 0;
      if (idx > 0) {
        const prev = path[idx - 1];
        const distM = haversineMeters(prev.lat, prev.lng, pt.lat, pt.lng);
        const timeSec = (pt.timestamp - prev.timestamp) / 1000;
        if (timeSec > 0) speedKmh = (distM / timeSec) * 3.6;
      }

      result.push({
        id: `wp_${pt.timestamp}_${idx}`,
        lat: pt.lat,
        lng: pt.lng,
        timestamp: pt.timestamp,
        speedKmh: parseFloat(speedKmh.toFixed(2)),
        formattedTime: formatTime(new Date(pt.timestamp)),
        formattedDate: formatWaypointDate(pt.timestamp),
      });
      lastTs = pt.timestamp;
    }
  });

  return result;
};

// ─── Map Legend Overlay ───────────────────────────────────────────────────────
const MapLegend = () => (
  <Box
    sx={{
      position: "absolute",
      bottom: 10,
      left: 10,
      background: "rgba(255,255,255,0.92)",
      borderRadius: "6px",
      px: 1.5,
      py: 0.8,
      display: "flex",
      gap: 1.5,
      alignItems: "center",
      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
      flexWrap: "wrap",
      zIndex: 5,
    }}
  >
    {[
      { color: "#34A853", label: "Start" },
      { color: "#FF6B00", label: "Route", line: true },
      { color: "#4285F4", label: "History Points" },
      { color: "#EA4335", label: "End" },
    ].map(({ color, label, line }) => (
      <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {line ? (
          <Box sx={{ width: 18, height: 4, borderRadius: 2, background: color }} />
        ) : (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: color,
              border: "1.5px solid #fff",
              boxShadow: `0 0 0 1px ${color}`,
            }}
          />
        )}
        <Typography sx={{ fontSize: 10, color: "#555" }}>{label}</Typography>
      </Box>
    ))}
  </Box>
);

// ─── Mini Map ─────────────────────────────────────────────────────────────────
const TripMiniMap = ({ historicalPath, loadingPath, waypointIntervalMs = 30000 }) => {
  const [miniMap, setMiniMap] = useState(null);
  const [selectedWaypoint, setSelectedWaypoint] = useState(null);

  // Sort points by timestamp so the polyline always connects them in chronological order
  const sortedPath = useMemo(
    () => [...historicalPath].sort((a, b) => a.timestamp - b.timestamp),
    [historicalPath],
  );

  // Fit bounds whenever map is ready OR sorted path changes
  useEffect(() => {
    setSelectedWaypoint(null);
    if (!miniMap || sortedPath.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    sortedPath.forEach((pt) => bounds.extend({ lat: pt.lat, lng: pt.lng }));
    miniMap.fitBounds(bounds, 40);
  }, [miniMap, sortedPath]);

  const waypoints = useMemo(
    () => sampleWaypoints(sortedPath, waypointIntervalMs),
    [sortedPath, waypointIntervalMs],
  );

  const center =
    sortedPath.length > 0
      ? sortedPath[Math.floor(sortedPath.length / 2)]
      : { lat: 0, lng: 0 };

  if (loadingPath) {
    return (
      <Box
        sx={{
          width: "100%", height: "100%", minHeight: 400,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "#f0f4f8", borderRadius: "20px", border: "1px solid #000000",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={32} sx={{ color: "#183059" }} />
          <Typography sx={{ mt: 1, fontSize: 12, color: "#666" }}>Loading path…</Typography>
        </Box>
      </Box>
    );
  }

  if (historicalPath.length === 0) {
    return (
      <Box
        sx={{
          width: "100%", height: "100%", minHeight: 400,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "#f0f4f8", borderRadius: "20px", border: "1px solid #000000",
        }}
      >
        <Typography sx={{ fontSize: 13, color: "#999" }}>No path data available</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%", height: "100%", minHeight: 400,
        borderRadius: "20px", overflow: "hidden",
        border: "1px solid #000000",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        position: "relative",
      }}
    >
      <GoogleMap
        mapContainerStyle={miniMapContainerStyle}
        center={center}
        zoom={13}
        onLoad={setMiniMap}
        options={{ ...mapOptions, minZoom: 1, styles: mapStyles }}
      >
        {/* Polyline drawn in chronological order */}
        <Polyline
          path={sortedPath}
          options={{ strokeColor: "#FF6B00", strokeOpacity: 0.9, strokeWeight: 4, zIndex: 10 }}
        />

        {waypoints.map((wp) => (
          <Marker
            key={wp.id}
            position={{ lat: wp.lat, lng: wp.lng }}
            onClick={() => setSelectedWaypoint(wp)}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: "#4285F4",
              fillOpacity: 0.9,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
            }}
            zIndex={20}
          />
        ))}

        {selectedWaypoint && (
          <InfoWindow
            position={{ lat: selectedWaypoint.lat, lng: selectedWaypoint.lng }}
            onCloseClick={() => setSelectedWaypoint(null)}
            options={{ pixelOffset: new window.google.maps.Size(0, -10) }}
          >
            <div
              style={{
                padding: 0,
                minWidth: 220,
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              }}
            >
              <div style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>
                  {selectedWaypoint.formattedDate}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#1a73e8" }}>
                  {selectedWaypoint.formattedTime}
                </div>
              </div>
              <div
                style={{
                  backgroundColor: "#34a853", color: "white",
                  padding: "6px 10px", borderRadius: 4,
                  textAlign: "center", fontSize: 13, fontWeight: 600, marginBottom: 10,
                }}
              >
                Speed: {selectedWaypoint.speedKmh} km/h
              </div>
              <div
                style={{
                  fontSize: 11, color: "#666", display: "flex",
                  gap: 4, alignItems: "flex-start",
                  paddingTop: 8, borderTop: "1px solid #e0e0e0",
                }}
              >
                <span style={{ color: "#ea4335" }}>📍</span>
                <span>
                  {selectedWaypoint.lat.toFixed(6)}, {selectedWaypoint.lng.toFixed(6)}
                </span>
              </div>
            </div>
          </InfoWindow>
        )}

        <Marker
          position={sortedPath[0]}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8, fillColor: "#34A853", fillOpacity: 1,
            strokeColor: "#fff", strokeWeight: 2,
          }}
          title="Trip Start"
          zIndex={30}
        />
        <Marker
          position={sortedPath[sortedPath.length - 1]}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8, fillColor: "#EA4335", fillOpacity: 1,
            strokeColor: "#fff", strokeWeight: 2,
          }}
          title="Trip End"
          zIndex={30}
        />
      </GoogleMap>
      <MapLegend />
    </Box>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value }) => (
  <Box
    sx={{
      display: "flex", alignItems: "center", gap: 1.5,
      p: "10px 14px", borderRadius: "8px",
      background: "#F4F6FA", border: "1px solid #E0E6EF",
    }}
  >
    <Box sx={{ color: "#183059", display: "flex" }}>{icon}</Box>
    <Box>
      <Typography
        sx={{
          fontSize: 10, color: "#888", fontWeight: 500,
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#183059", mt: "1px" }}>
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

// ─── Expanded Trip Details Panel ──────────────────────────────────────────────
const TripExpandedRow = ({
  trip,
  historicalPath,
  loadingPath,
  waypointIntervalMs = 30000,
  vehicle,
  route,
}) => (
  <Box
    sx={{
      display: "flex", gap: 2.5, p: "16px 20px 20px",
      background: "linear-gradient(135deg, #F8FBFF 0%, #EEF4FB 100%)",
      borderTop: "2px solid #D0E8F7",
    }}
  >
    {/* ── Left: Details ── */}
    <Box sx={{ flex: "0 0 42%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <RouteIcon sx={{ fontSize: 18, color: "#183059" }} />
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#183059" }}>
          Trip #{trip.id} Details
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}>
        {vehicle?.name && (
          <StatCard
            icon={<SpeedIcon sx={{ fontSize: 18 }} />}
            label="Vehicle Name"
            value={vehicle.name}
          />
        )}
        {(vehicle?.VehicleNumber || vehicle?.vehicleNumber) && (
          <StatCard
            icon={<SpeedIcon sx={{ fontSize: 18 }} />}
            label="Vehicle Number"
            value={vehicle?.VehicleNumber || vehicle?.vehicleNumber}
          />
        )}
        <StatCard
          icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
          label="Date"
          value={formatTripDate(trip.date)}
        />
        <StatCard
          icon={<TimerIcon sx={{ fontSize: 18 }} />}
          label="Duration"
          value={trip.duration || "—"}
        />
        <StatCard
          icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
          label="Start Time"
          value={formatTripTime(trip.startTime)}
        />
        <StatCard
          icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
          label="End Time"
          value={formatTripTime(trip.endTime)}
        />
        <StatCard
          icon={<RouteIcon sx={{ fontSize: 18 }} />}
          label="Route"
          value={route?.name || "—"}
        />
      </Box>
    </Box>

    {/* ── Right: Mini Map ── */}
    <Box sx={{ flex: 1, minHeight: 400 }}>
      <TripMiniMap
        historicalPath={historicalPath}
        loadingPath={loadingPath}
        waypointIntervalMs={waypointIntervalMs}
      />
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const PreviousTripsTable = ({
  trips,
  loading,
  selectedTrip,
  onTripClick,
  onClearSelection,
  loadingPath,
  historicalPath = [],
  isLoaded = false,
  historyPointInterval = 30000,
  vehicle = null,
  route = null,
}) => {
  const [expandedId, setExpandedId] = useState(null);

  const handleRowClick = (trip) => {
    const isSame = expandedId === trip.id;
    if (isSame) {
      setExpandedId(null);
      onClearSelection();
    } else {
      setExpandedId(trip.id);
      onTripClick(trip);
    }
  };

  return (
    <Box sx={{ borderRadius: "4px", border: "1px solid #A5DCF3" }}>
      <TableContainer
        component={Paper}
        sx={{ boxShadow: "0px 2px 4px rgba(0,0,0,0.1)", maxHeight: 600 }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {["", "Sr. No", "Date", "Start Time", "End Time", "Duration"].map(
                (label, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: "#E8EAEE",
                      color: "#183059",
                      ...(i === 0 && { width: 40 }),
                    }}
                  >
                    {label}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : trips.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">No trips found for today</Typography>
                </TableCell>
              </TableRow>
            ) : (
              trips.map((trip, index) => {
                const isExpanded = expandedId === trip.id;
                const isSelected = selectedTrip?.id === trip.id;

                return (
                  <React.Fragment key={trip.id}>
                    {/* ── Summary Row ── */}
                    <TableRow
                      onClick={() => handleRowClick(trip)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#FFF3E0" : "inherit",
                        "&:hover": { backgroundColor: isSelected ? "#FFE0B2" : "#F5F5F5" },
                        borderLeft: isExpanded ? "3px solid #FF6B00" : "3px solid transparent",
                        transition: "background-color 0.15s, border-left 0.15s",
                      }}
                    >
                      <TableCell sx={{ py: 0.8, pl: 1.5, color: "#888" }}>
                        {isExpanded ? (
                          <KeyboardArrowUpIcon fontSize="small" sx={{ color: "#FF6B00" }} />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{formatTripDate(trip.date)}</TableCell>
                      <TableCell>{formatTripTime(trip.startTime)}</TableCell>
                      <TableCell>{formatTripTime(trip.endTime)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                          {trip.duration}
                          {isExpanded && (
                            <Box
                              sx={{
                                width: 6, height: 6, borderRadius: "50%",
                                background: "#FF6B00",
                                animation: "pulse 1.4s infinite",
                                "@keyframes pulse": {
                                  "0%,100%": { opacity: 1 },
                                  "50%": { opacity: 0.3 },
                                },
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* ── Expanded Detail Row ── */}
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ p: 0, borderBottom: isExpanded ? "2px solid #D0E8F7" : "none" }}
                      >
                        <Collapse in={isExpanded} timeout={300} unmountOnExit>
                          {isLoaded ? (
                            <TripExpandedRow
                              trip={trip}
                              historicalPath={isSelected ? historicalPath : []}
                              loadingPath={isSelected ? loadingPath : false}
                              waypointIntervalMs={historyPointInterval}
                              vehicle={vehicle}
                              route={route}
                            />
                          ) : (
                            <Box sx={{ p: 3, textAlign: "center" }}>
                              <CircularProgress size={20} />
                            </Box>
                          )}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PreviousTripsTable;